import type { WebSearchToolCompany } from "@shaarp/common";

export type CompanyListState = {
  readonly rows: WebSearchToolCompany[];
  readonly total: number;
  readonly targetCount?: number;
  /**
   * Companies the model is writing into a call the server has not answered
   * yet: not rows until it takes them, but the work the card shows meanwhile.
   */
  readonly writingCount?: number;
};

type ToolPartLike = {
  readonly type: string;
  readonly state?: string;
  readonly output?: unknown;
  readonly input?: unknown;
  /** Read like a settled output: its rows are the ones found so far. */
  readonly preliminary?: boolean;
};

type SettledOutput = {
  readonly rows: WebSearchToolCompany[];
  readonly total?: number;
  readonly targetCount?: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function finiteNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}

function optionalText(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * A row the card can show: a name and the page that names it. Every field is
 * re-read by type, so a stray object in a text field never reaches the render.
 */
function toCompanyRow(value: unknown): WebSearchToolCompany | null {
  if (!isRecord(value)) return null;
  const { name, sourceUrl } = value;
  if (typeof name !== "string" || name.trim().length === 0) return null;
  if (typeof sourceUrl !== "string") return null;
  return {
    name,
    sourceUrl,
    domain: optionalText(value.domain),
    city: optionalText(value.city),
    specialty: optionalText(value.specialty),
    estimate: optionalText(value.estimate),
    ...(value.isCloseMatch === true && { isCloseMatch: true }),
  };
}

/**
 * Parts are persisted data, and a streamed part can be anything: an output
 * that is not an `addCompanies` answer is left out rather than thrown on.
 */
function readSettledOutput(output: unknown): SettledOutput | null {
  if (!isRecord(output) || !Array.isArray(output.added)) return null;
  const rows = output.added.flatMap((row) => {
    const company = toCompanyRow(row);
    return company ? [company] : [];
  });
  return {
    rows,
    total: finiteNumber(output.total),
    targetCount: finiteNumber(output.targetCount),
  };
}

/**
 * The research card's list, read off a message's addCompanies parts: each
 * call's accepted rows, appended in order — the API already folded the
 * duplicates. The last settled call carries the count and the target; the
 * count is never below the rows shown.
 */
export function collectCompanyList(
  parts: ReadonlyArray<ToolPartLike>
): CompanyListState {
  const settled = parts
    .filter(
      (part) =>
        part.type === "tool-addCompanies" && part.state === "output-available"
    )
    .flatMap((part) => {
      const output = readSettledOutput(part.output);
      return output ? [output] : [];
    });
  const last = settled[settled.length - 1];
  const rows = settled.flatMap((output) => output.rows);
  const writing = parts
    .filter(
      (part) =>
        part.type === "tool-addCompanies" &&
        (part.state === "input-streaming" || part.state === "input-available")
    )
    .map((part) => (isRecord(part.input) ? part.input : {}));
  // The input streams in token by token: a company counts once its name has
  // started, never on the empty object that opens it.
  const writingCount = writing
    .flatMap((input) =>
      Array.isArray(input.companies) ? input.companies : []
    )
    .filter(
      (company) =>
        isRecord(company) &&
        typeof company.name === "string" &&
        company.name.trim().length > 0
    ).length;
  const targetCount =
    last?.targetCount ??
    writing
      .map((input) => finiteNumber(input.targetCount))
      .find((target) => target !== undefined);

  return {
    rows,
    // A manual resume restarts the session's count, not the card's rows.
    total: Math.max(last?.total ?? 0, rows.length),
    ...(targetCount !== undefined && { targetCount }),
    ...(writingCount > 0 && { writingCount }),
  };
}
